import type { Comparer } from '@reduxjs/toolkit';
import { createEntityAdapter, createSlice } from '@reduxjs/toolkit';

import type { RootState } from '@polyai/stream/store';
import {
  isDeleteAttribute,
  isDeleteFunction,
  isDeleteHandoff,
  isDeleteTemplate,
} from '@polyai/stream/store/matchers';
import { createReferenceAdapter } from '@polyai/stream/store/references';
import { assignDefinedFields } from '@polyai/stream/store/utils/assign-fields';
import { assignMetadata } from '@polyai/stream/store/utils/assign-metadata';

import { deleteFunction } from '../functions/actions';
import { deleteTranslation } from '../translations/actions';
import {
  createTopic,
  deleteTopic,
  importTopics,
  setEmbeddingModel,
  toggleTopicStatus,
  updateTopic,
} from './actions';
import type { KnowledgeBase, Topic, TopicReferences } from './types';

const topicReferences = createReferenceAdapter([
  'sms',
  'handoff',
  'attributes',
  'globalFunctions',
  'variables',
  'translations',
]);

const topicComparer: Comparer<Topic> = (topic1, topic2) => {
  return topic1.updatedAt && topic2.updatedAt
    ? new Date(topic2.updatedAt).getTime() -
        new Date(topic1.updatedAt).getTime()
    : 0;
};

const topicAdapter = createEntityAdapter<Topic>({
  sortComparer: topicComparer,
});

const uninstantiatedTopicAdapter = createEntityAdapter<Topic>();

const initialState = {
  topics: topicAdapter.getInitialState(),
  embeddingModel: '',
  uninstantiatedTopics: uninstantiatedTopicAdapter.getInitialState(),
} satisfies KnowledgeBase;

const slice = createSlice({
  name: 'knowledgeBase',
  initialState,
  reducers: {},
  // all reducers are here because actions are defined externally
  extraReducers: builder => {
    // main slice reducers
    builder.addCase(createTopic, (state, action) => {
      // isActive uses `optional bool` in proto, so:
      // - undefined = old event (field not present) → default to true
      // - true/false = new event (explicitly set) → use that value
      const isActive = action.payload.isActive ?? true;

      const topic: Topic = {
        ...action.payload,
        exampleQueries: action.payload.exampleQueries.queries.map(query => {
          return {
            query,
          };
        }),
        isActive,
        createdAt: '',
        createdBy: '',
        updatedAt: '',
        updatedBy: '',
      };

      assignMetadata(action, topic);
      topicAdapter.addOne(state.topics, topic);
    });
    builder.addCase(updateTopic, (state, action) => {
      let topic = topicSelectors.selectById(state.topics, action.payload.id);

      if (!topic) {
        topic = {} as unknown as Topic;
        topic.updatedAt = '';
        topic.updatedBy = '';
        topic.createdAt = '';
        topic.createdBy = '';
        topic.isActive = true;
      }

      const { exampleQueries, references, ...rest } = action.payload;

      // isActive uses `optional bool` in proto, so:
      // - undefined = old event (field not present) → don't update (preserve existing)
      // - true/false = new event (explicitly set) → update to that value
      // assignDefinedFields only copies fields that are not undefined, which handles this automatically

      const updatedTopic = assignDefinedFields({ ...topic }, {
        ...rest,
        exampleQueries: exampleQueries?.queries?.map(query => {
          return { query };
        }),
      } as Partial<Topic>);

      if (references !== undefined) {
        updatedTopic.references =
          topicReferences.createInitialState(references);
      }

      // Ensure isActive defaults to true if somehow still not set (e.g., new topic via upsert)
      if (
        updatedTopic.isActive === undefined ||
        updatedTopic.isActive === null
      ) {
        updatedTopic.isActive = true;
      }

      assignMetadata(action, updatedTopic);
      topicAdapter.upsertOne(state.topics, updatedTopic);
    });
    builder.addCase(deleteTopic, (state, action) => {
      topicAdapter.removeOne(state.topics, action.payload.id);
    });
    builder.addCase(toggleTopicStatus, (state, action) => {
      const topic = topicSelectors.selectById(state.topics, action.payload.id);

      if (topic) {
        const updatedTopic = {
          ...topic,
          isActive: action.payload.isActive,
        };

        assignMetadata(action, updatedTopic);
        topicAdapter.upsertOne(state.topics, updatedTopic);
      }
    });

    builder.addCase(importTopics, (state, action) => {
      const { created, updated, deleted } = action.payload;

      // Handle created topics
      for (const topicData of created) {
        // isActive uses `optional bool` in proto, so:
        // - undefined = old event (field not present) → default to true
        // - true/false = new event (explicitly set) → use that value
        const isActive = topicData.isActive ?? true;

        const topic: Topic = {
          ...topicData,
          id: topicData.id || crypto.randomUUID(),
          exampleQueries: topicData.exampleQueries.queries.map(query => ({
            query,
          })),
          isActive,
          createdAt: '',
          createdBy: '',
          updatedAt: '',
          updatedBy: '',
          references: topicReferences.createInitialState(),
        };
        assignMetadata(action, topic);
        topicAdapter.addOne(state.topics, topic);
      }

      // Handle updated topics
      for (const topicData of updated) {
        const existingTopic = topicSelectors.selectById(
          state.topics,
          topicData.id,
        );
        const { exampleQueries, ...rest } = topicData;

        // isActive uses `optional bool` in proto, so assignDefinedFields
        // will only update it if explicitly set (not undefined)
        const updatedTopic = assignDefinedFields({ ...existingTopic }, {
          ...rest,
          exampleQueries: exampleQueries?.queries?.map(query => ({
            query,
          })),
        } as Partial<Topic>);

        // Ensure isActive defaults to true if somehow still not set
        if (
          updatedTopic.isActive === undefined ||
          updatedTopic.isActive === null
        ) {
          updatedTopic.isActive = true;
        }

        assignMetadata(action, updatedTopic);
        topicAdapter.upsertOne(state.topics, updatedTopic);
      }

      // Handle deleted topics
      for (const topicId of deleted) {
        topicAdapter.removeOne(state.topics, topicId);
      }
    });

    builder.addCase(setEmbeddingModel, (state, action) => {
      state.embeddingModel = action.payload.embeddingModel;
    });

    // Remove references to the deleted function from all steps in all flows
    builder.addCase(deleteFunction, (state, action) => {
      const { id: functionId } = action.payload;
      Object.values(state.topics.entities).forEach(topic => {
        if (topic.references.globalFunctions[functionId]) {
          delete topic.references.globalFunctions[functionId];
        }
      });
    });

    // Remove references to deleted translation from all topics
    builder.addCase(deleteTranslation, (state, action) => {
      const { id: translationId } = action.payload;
      for (const topic of Object.values(state.topics.entities)) {
        if (topic.references.translations?.[translationId]) {
          delete topic.references.translations[translationId];
        }
      }
    });

    // other reducers
    builder.addMatcher(isDeleteTemplate, (state, action) => {
      const referencedTopics = topicReferences.selectors.selectParentsByRef(
        state.topics,
        'sms',
        action.payload.id,
      );

      for (const referencedTopic of referencedTopics) {
        topicReferences.removeReference(
          state.topics,
          referencedTopic,
          'sms',
          action.payload.id,
        );
      }
    });
    builder.addMatcher(isDeleteHandoff, (state, action) => {
      const referencedTopics = topicReferences.selectors.selectParentsByRef(
        state.topics,
        'handoff',
        action.payload.id,
      );
      for (const referencedTopic of referencedTopics) {
        topicReferences.removeReference(
          state.topics,
          referencedTopic,
          'handoff',
          action.payload.id,
        );
      }
    });
    builder.addMatcher(isDeleteAttribute, (state, action) => {
      const referencedTopics = topicReferences.selectors.selectParentsByRef(
        state.topics,
        'attributes',
        action.payload.id,
      );
      for (const referencedTopic of referencedTopics) {
        topicReferences.removeReference(
          state.topics,
          referencedTopic,
          'attributes',
          action.payload.id,
        );
      }
    });
    builder.addMatcher(isDeleteFunction, (state, action) => {
      const referencedTopics = topicReferences.selectors.selectParentsByRef(
        state.topics,
        'globalFunctions',
        action.payload.id,
      );
      for (const referencedTopic of referencedTopics) {
        topicReferences.removeReference(
          state.topics,
          referencedTopic,
          'globalFunctions',
          action.payload.id,
        );
      }
    });
  },
});

export type { KnowledgeBase, Topic, TopicReferences };

export { topicAdapter, topicReferences, uninstantiatedTopicAdapter };

export {
  createTopic,
  deleteTopic,
  importTopics,
  setEmbeddingModel,
  toggleTopicStatus,
  updateTopic,
};

export const topicSelectors = topicAdapter.getSelectors();

export const selectKnowledgeBase = (state: RootState) => state.knowledgeBase;

export default slice.reducer;
